import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";

/** Kit contractual (superadmin): plantillas de contrato piloto, SLA, DPA y
 *  condiciones con placeholders rellenables por organización, preview y PDF. */

type DocMeta = { key: string; title: string; description: string; placeholders: string[] };
type Org = { id: string; name: string };

const FIELD_LABELS: Record<string, string> = {
  ORG_NAME: "Organización", ORG_CIF: "NIF/CIF del cliente", ORG_DOMICILIO: "Domicilio del cliente",
  CONTACTO_NOMBRE: "Firmante del cliente", CONTACTO_EMAIL: "Email del firmante",
  PLAN: "Plan", PRECIO_MENSUAL: "Precio mensual (€)", DURACION_MESES: "Duración (meses)",
  MAX_USUARIOS: "Máx. usuarios", PERIODICIDAD_FACTURACION: "Periodicidad de facturación",
  FECHA: "Fecha", LUGAR_FIRMA: "Lugar de firma", JURISDICCION: "Jurisdicción",
  HALTRIX_NIF: "NIF de Haltrix", HALTRIX_DOMICILIO: "Domicilio de Haltrix",
  EMAIL_SOPORTE: "Email de soporte", VERSION_CONDICIONES: "Versión de las condiciones",
  PROVEEDOR_HOSTING: "Proveedor del VPS",
};

const inp = (extra?: React.CSSProperties): React.CSSProperties => ({
  background: "var(--clr-input-bg)", border: "1px solid var(--clr-border)", borderRadius: 12,
  color: "var(--clr-text)", padding: "7px 10px", fontSize: "0.8rem", outline: "none",
  width: "100%", boxSizing: "border-box" as const, ...extra,
});

/** Render mínimo del markdown de las plantillas para el preview. */
function MdPreview({ md }: { md: string }) {
  const blocks = useMemo(() => md.split("\n"), [md]);
  const inline = (s: string) =>
    s.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      part.startsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : part);
  return (
    <div style={{ fontSize: "0.8rem", lineHeight: 1.65, color: "var(--clr-text2)" }}>
      {blocks.map((line, i) => {
        const t = line.trim();
        if (!t) return null;
        if (t.startsWith("# ")) return <h2 key={i} style={{ fontSize: "1.05rem", color: "var(--clr-text)", margin: "14px 0 6px" }}>{inline(t.slice(2))}</h2>;
        if (t.startsWith("## ")) return <h3 key={i} style={{ fontSize: "0.92rem", color: "var(--clr-text)", margin: "14px 0 4px" }}>{inline(t.slice(3))}</h3>;
        if (t.startsWith("### ")) return <h4 key={i} style={{ fontSize: "0.84rem", color: "var(--clr-text)", margin: "10px 0 3px" }}>{inline(t.slice(4))}</h4>;
        if (t === "---") return <hr key={i} style={{ border: "none", borderTop: "1px solid var(--clr-border2)", margin: "12px 0" }} />;
        if (t.startsWith("|")) return <div key={i} style={{ fontFamily: "monospace", fontSize: "0.7rem", whiteSpace: "pre", overflowX: "auto" }}>{t}</div>;
        if (/^[-*] /.test(t)) return <div key={i} style={{ paddingLeft: 16 }}>• {inline(t.slice(2))}</div>;
        return <p key={i} style={{ margin: "4px 0" }}>{inline(t)}</p>;
      })}
    </div>
  );
}

export default function SuperAdminContratosPage() {
  const [docs, setDocs] = useState<DocMeta[]>([]);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [activeKey, setActiveKey] = useState<string>("contrato-piloto");
  const [orgId, setOrgId] = useState<string>("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<string>("");
  const [missing, setMissing] = useState<string[]>([]);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get<{ docs: DocMeta[] }>("/superadmin/legal/docs").then(d => setDocs(d.docs)).catch(() => setError("No se pudieron cargar las plantillas"));
    api.get<{ items?: Org[] } | Org[]>("/organizations")
      .then((d: any) => setOrgs(Array.isArray(d) ? d : d.items ?? []))
      .catch(() => {});
  }, []);

  const active = docs.find(d => d.key === activeKey);

  // Re-render del preview al cambiar doc, org o valores (con debounce ligero)
  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => {
      api.post<{ markdown: string; missing: string[] }>(
        `/superadmin/legal/docs/${active.key}/render`,
        { values, org_id: orgId || null, pdf: false },
      ).then(r => { setPreview(r.markdown); setMissing(r.missing); })
        .catch(() => setError("Error al generar el preview"));
    }, 350);
    return () => clearTimeout(t);
  }, [activeKey, orgId, values, active]);

  const downloadPdf = async () => {
    if (!active) return;
    setDownloading(true);
    try {
      const token = useAuthStore.getState().accessToken;
      const resp = await fetch(`/api/superadmin/legal/docs/${active.key}/render`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ values, org_id: orgId || null, pdf: true }),
      });
      if (!resp.ok) throw new Error("Error generando el PDF");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = (resp.headers.get("Content-Disposition")?.match(/filename="(.+)"/)?.[1])
        || `helixscan_${active.key}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setDownloading(false);
    }
  };

  const panel: React.CSSProperties = {
    background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
    borderRadius: 18, backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
    boxShadow: "var(--clr-shadow-sm)",
  };

  return (
    <div className="page" style={{ color: "var(--clr-text)", fontVariantNumeric: "tabular-nums" }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em" }}>Contratos</h1>
        <p style={{ margin: "4px 0 0", fontSize: "0.82rem", color: "var(--clr-text3)" }}>
          Kit contractual para cerrar pilotos: rellena, revisa y descarga en PDF.
        </p>
      </div>

      <div style={{
        ...panel, borderRadius: 12,
        padding: "8px 14px", marginBottom: 16, fontSize: "0.78rem", color: "var(--clr-text2)",
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <span style={{ width: 7, height: 7, borderRadius: 4, background: "#f59e0b", display: "inline-block", flexShrink: 0 }} />
        <span>
          Estas plantillas son borradores de trabajo preparados por Haltrix —
          <strong> revísalas con un abogado antes de firmar</strong> con un cliente.
        </span>
      </div>

      {error && <div style={{ color: "#ef4444", fontSize: "0.82rem", marginBottom: 12 }}>{error}</div>}

      {/* Selector de documento */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {docs.map(d => (
          <button key={d.key} onClick={() => setActiveKey(d.key)} title={d.description}
            style={{
              background: d.key === activeKey ? "var(--clr-org-primary)" : "var(--clr-surface)",
              color: d.key === activeKey ? "#fff" : "var(--clr-text3)",
              border: d.key === activeKey ? "1px solid transparent" : "1px solid var(--clr-border)",
              borderRadius: 999,
              padding: "0.42rem 1rem", fontSize: "0.78rem",
              fontWeight: d.key === activeKey ? 600 : 500, cursor: "pointer",
              transition: "all 0.15s",
            }}>
            {d.title}
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(260px, 340px) 1fr", gap: 16, alignItems: "start" }}>
        {/* Formulario */}
        <div style={{ ...panel, padding: "1rem" }}>
          <div style={{ fontSize: "0.66rem", fontWeight: 600, color: "var(--clr-text4)",
            textTransform: "uppercase", letterSpacing: 0.9, marginBottom: 10 }}>Datos del documento</div>

          <label style={{ display: "block", marginBottom: 10 }}>
            <span style={{ fontSize: "0.72rem", color: "var(--clr-text3)", display: "block", marginBottom: 3 }}>Organización (precarga nombre, contacto y plan)</span>
            <select value={orgId} onChange={e => setOrgId(e.target.value)} style={inp()}>
              <option value="">— Sin organización (plantilla) —</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>

          {(active?.placeholders ?? []).map(ph => (
            <label key={ph} style={{ display: "block", marginBottom: 8 }}>
              <span style={{ fontSize: "0.72rem", color: "var(--clr-text3)", display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
                {missing.includes(ph) && <span style={{ width: 6, height: 6, borderRadius: 3, background: "#f59e0b", display: "inline-block", flexShrink: 0 }} />}
                {FIELD_LABELS[ph] ?? ph}{missing.includes(ph) ? " · pendiente" : ""}
              </span>
              <input value={values[ph] ?? ""} placeholder={missing.includes(ph) ? "(sin valor)" : "(usar valor por defecto)"}
                onChange={e => setValues(v => ({ ...v, [ph]: e.target.value }))} style={inp()} />
            </label>
          ))}

          <button onClick={downloadPdf} disabled={downloading}
            style={{ width: "100%", marginTop: 8, background: "var(--clr-org-primary)", color: "#fff",
              border: 0, borderRadius: 999, padding: "10px 0", fontSize: "0.85rem",
              fontWeight: 600, cursor: downloading ? "wait" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
            {!downloading && (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3v12m0 0l-4.5-4.5M12 15l4.5-4.5M4 19h16"/>
              </svg>
            )}
            {downloading ? "Generando…" : "Descargar PDF"}
          </button>
          {missing.length > 0 && (
            <div style={{ marginTop: 8, fontSize: "0.7rem", color: "var(--clr-text3)", display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: 3, background: "#f59e0b", display: "inline-block", flexShrink: 0 }} />
              {missing.length} campo{missing.length !== 1 ? "s" : ""} sin rellenar — saldrá[n] como [CAMPO] en el documento.
            </div>
          )}
        </div>

        {/* Preview */}
        <div style={{ ...panel, padding: "1.25rem 1.5rem", maxHeight: "72vh", overflowY: "auto" }}>
          {preview ? <MdPreview md={preview} /> : <span style={{ color: "var(--clr-text4)", fontSize: "0.82rem" }}>Cargando…</span>}
        </div>
      </div>
    </div>
  );
}
